import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Animated,
  Pressable,
  StyleSheet,
  type GestureResponderEvent,
  type NativeSyntheticEvent,
  type PressableProps,
  type PressableStateCallbackType,
  type StyleProp,
  type TargetedEvent,
  type ViewStyle,
} from "react-native";
import { useAppConfig } from "../../../../AppConfigProvider";

const SWITCH_SIZES = {
  s: { width: 36, height: 20, padding: 2, thumb: 16 },
  m: { width: 44, height: 24, padding: 3, thumb: 18 },
  l: { width: 54, height: 30, padding: 3, thumb: 24 },
} as const;

const DEFAULT_INACTIVE_TRACK_COLOR = "#D1D5DB";
const DEFAULT_THUMB_ACTIVE_COLOR = "#FFFFFF";
const DEFAULT_THUMB_INACTIVE_COLOR = "#F4F4F5";

type SwitchSize = keyof typeof SWITCH_SIZES;
type SwitchColorOverrides = string | { true?: string; false?: string; on?: string; off?: string };
type SwitchColorSet = { true: string; false: string };
type ElementStyleProp = StyleProp<ViewStyle> | ((state: { checked: boolean }) => StyleProp<ViewStyle>);

export interface CustomSwitchProps extends Omit<PressableProps, "style"> {
  value?: boolean;
  onValueChange?: (nextValue: boolean) => void;
  disabled?: boolean;
  size?: SwitchSize;
  trackColor?: SwitchColorOverrides;
  thumbColor?: SwitchColorOverrides;
  style?: PressableProps["style"];
  trackStyle?: ElementStyleProp;
  thumbStyle?: ElementStyleProp;
}

const resolveColorSet = (colorProp: SwitchColorOverrides | undefined, defaults: SwitchColorSet): SwitchColorSet => {
  if (typeof colorProp === "string") {
    return { true: colorProp, false: colorProp };
  }

  if (colorProp != null && typeof colorProp === "object") {
    return {
      true: colorProp.true ?? colorProp.on ?? defaults.true,
      false: colorProp.false ?? colorProp.off ?? defaults.false,
    };
  }

  return defaults;
};

const resolveElementStyle = (styleProp: ElementStyleProp | undefined, state: { checked: boolean }) => {
  if (typeof styleProp === "function") {
    return styleProp(state);
  }

  return styleProp;
};

const CustomSwitch: React.FC<CustomSwitchProps> = (props) => {
  const {
    value = false,
    onValueChange,
    disabled = false,
    size = "m",
    trackColor,
    thumbColor,
    style: incomingPressableStyle,
    trackStyle,
    thumbStyle,
    onPress: pressableOnPress,
    onFocus: pressableOnFocus,
    onBlur: pressableOnBlur,
    accessibilityLabel: propAccessibilityLabel,
    accessibilityHint: propAccessibilityHint,
    ...restProps
  } = props;
  const { primaryColor } = useAppConfig();
  const [isFocused, setIsFocused] = useState(false);
  const sizeConfig = SWITCH_SIZES[size] ?? SWITCH_SIZES.m;
  const animationValue = useRef(new Animated.Value(value ? 1 : 0)).current;

  useEffect(() => {
    if (disabled && isFocused) {
      setIsFocused(false);
    }
  }, [disabled, isFocused]);

  const animateToValue = useCallback(
    (nextValue: boolean) => {
      Animated.timing(animationValue, {
        toValue: nextValue ? 1 : 0,
        duration: 180,
        useNativeDriver: false,
      }).start();
    },
    [animationValue]
  );

  useEffect(() => {
    animateToValue(value);
  }, [animateToValue, value]);

  const trackColors = useMemo<SwitchColorSet>(
    () =>
      resolveColorSet(trackColor, {
        true: primaryColor,
        false: DEFAULT_INACTIVE_TRACK_COLOR,
      }),
    [trackColor, primaryColor]
  );

  const thumbColors = useMemo<SwitchColorSet>(
    () =>
      resolveColorSet(thumbColor, {
        true: DEFAULT_THUMB_ACTIVE_COLOR,
        false: DEFAULT_THUMB_INACTIVE_COLOR,
      }),
    [thumbColor]
  );

  const handleToggle = useCallback(
    (event?: GestureResponderEvent) => {
      if (disabled) {
        return;
      }

      pressableOnPress?.(event as GestureResponderEvent);

      const nextValue = !value;
      animateToValue(nextValue);
      onValueChange?.(nextValue);
    },
    [animateToValue, disabled, onValueChange, pressableOnPress, value]
  );

  const handleFocus = useCallback(
    (event: NativeSyntheticEvent<TargetedEvent>) => {
      if (!disabled) {
        setIsFocused(true);
      }
      pressableOnFocus?.(event);
    },
    [disabled, pressableOnFocus]
  );

  const handleBlur = useCallback(
    (event: NativeSyntheticEvent<TargetedEvent>) => {
      setIsFocused(false);
      pressableOnBlur?.(event);
    },
    [pressableOnBlur]
  );

  const resolvePressableStyle = useCallback(
    (state: PressableStateCallbackType): StyleProp<ViewStyle> => {
      const resolvedIncomingStyle =
        typeof incomingPressableStyle === "function" ? incomingPressableStyle(state) : incomingPressableStyle;

      return [
        styles.wrapper,
        disabled && styles.disabledWrapper,
        !disabled && isFocused ? styles.focusedWrapper : null,
        !disabled && isFocused ? { shadowColor: primaryColor } : null,
        state.pressed && !disabled ? styles.pressedWrapper : null,
        resolvedIncomingStyle,
      ];
    },
    [disabled, incomingPressableStyle, isFocused, primaryColor]
  );

  const resolvedTrackStyle = resolveElementStyle(trackStyle, { checked: value });
  const resolvedThumbStyle = resolveElementStyle(thumbStyle, { checked: value });
  const thumbTranslateX = animationValue.interpolate({
    inputRange: [0, 1],
    outputRange: [0, Math.max(0, sizeConfig.width - sizeConfig.padding * 2 - sizeConfig.thumb)],
  });
  const trackBackgroundColor = animationValue.interpolate({
    inputRange: [0, 1],
    outputRange: [trackColors.false, trackColors.true],
  });
  const thumbBackgroundColor = animationValue.interpolate({
    inputRange: [0, 1],
    outputRange: [thumbColors.false, thumbColors.true],
  });

  const accessibilityLabel = propAccessibilityLabel || restProps["aria-label"] || "Toggle switch";
  const accessibilityHint = propAccessibilityHint || "Toggle the switch";
  const accessibilityState = {
    ...(restProps.accessibilityState ?? {}),
    checked: value,
    disabled,
  };

  return (
    <Pressable
      accessibilityRole="switch"
      accessibilityLabel={accessibilityLabel}
      accessibilityHint={accessibilityHint}
      role="switch"
      aria-checked={value}
      aria-disabled={disabled}
      aria-label={accessibilityLabel}
      focusable={!disabled}
      onPress={handleToggle}
      onFocus={handleFocus}
      onBlur={handleBlur}
      style={resolvePressableStyle}
      {...restProps}
      accessibilityState={accessibilityState}>
      <Animated.View
        pointerEvents="none"
        style={[
          styles.track,
          {
            width: sizeConfig.width,
            height: sizeConfig.height,
            borderRadius: sizeConfig.height / 2,
            padding: sizeConfig.padding,
          },
          { backgroundColor: trackBackgroundColor },
          resolvedTrackStyle,
        ]}>
        <Animated.View
          style={[
            styles.thumb,
            {
              width: sizeConfig.thumb,
              height: sizeConfig.thumb,
              borderRadius: sizeConfig.thumb / 2,
              transform: [{ translateX: thumbTranslateX }],
              backgroundColor: thumbBackgroundColor,
            },
            resolvedThumbStyle,
          ]}
        />
      </Animated.View>
    </Pressable>
  );
};

export default CustomSwitch;

const styles = StyleSheet.create({
  wrapper: {
    alignSelf: "flex-start",
  },
  disabledWrapper: {
    opacity: 0.4,
  },
  pressedWrapper: {
    opacity: 0.8,
  },
  focusedWrapper: {
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.4,
    shadowRadius: 4,
    elevation: 2,
  },
  track: {
    flexDirection: "row",
    alignItems: "center",
  },
  thumb: {
    shadowColor: "#000",
    shadowOpacity: 0.15,
    shadowRadius: 2,
    shadowOffset: { width: 0, height: 2 },
    elevation: 1,
  },
});
